import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SECTIONS, type SectionKey } from './sections';
import { StatePill } from './components';

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

export interface MetricStripItem {
  readonly label: string;
  readonly value: ReactNode;
  readonly note?: ReactNode;
  readonly to?: string;
  readonly tone?: 'default' | 'pos' | 'neg' | 'warn';
}

export function MetricStrip({
  section,
  items,
  columns = 3,
  className,
}: {
  section: SectionKey;
  items: readonly MetricStripItem[];
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const colClass =
    columns === 2
      ? 'grid-cols-2'
      : columns === 4
      ? 'grid-cols-2 sm:grid-cols-4'
      : 'grid-cols-3';

  return (
    <div className={cx('native-screen-enter grid gap-2.5 sm:gap-3', colClass, className)}>
      {items.map((item) => (
        <MetricCell key={item.label} section={section} item={item} />
      ))}
    </div>
  );
}

function MetricCell({
  section,
  item,
}: {
  section: SectionKey;
  item: MetricStripItem;
}) {
  const sec = SECTIONS[section];
  const toneClass =
    item.tone === 'neg'
      ? 'text-neg'
      : item.tone === 'pos'
      ? 'text-pos'
      : item.tone === 'warn'
      ? 'text-warn'
      : 'text-ink';

  const body = (
    <div
      className="app-shell-surface overflow-hidden rounded-[22px] border border-border px-3.5 py-3.5 shadow-sm backdrop-blur-xl transition-all duration-150 hover:-translate-y-px"
      style={{ borderColor: sec.edge }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex min-w-0 items-center gap-2">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: sec.color }}
            aria-hidden
          />
          <div className="truncate text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">{item.label}</div>
        </div>
        {item.to && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" strokeWidth={1.9} />}
      </div>
      <div className={cx('mt-3 min-w-0 truncate text-[clamp(1rem,4.6vw,1.78rem)] font-semibold tabular-nums tracking-[-0.02em] leading-none', toneClass)}>
        {item.value}
      </div>
      {item.note && <div className="mt-1.5 text-caption leading-5 text-ink-muted">{item.note}</div>}
    </div>
  );

  return item.to ? (
    <Link to={item.to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

export function ActionQueueCard({
  section,
  eyebrow,
  title,
  description,
  meta,
  actions,
  tone = 'accent',
  className,
}: {
  section: SectionKey;
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  tone?: 'accent' | 'warn' | 'danger' | 'neutral';
  className?: string;
}) {
  const sec = SECTIONS[section];
  const styles =
    tone === 'danger'
      ? {
          background: 'linear-gradient(160deg, rgba(255,255,255,0.24) 0%, rgba(255,236,234,0.96) 100%)',
          accent: 'var(--neg)',
          label: 'text-neg',
          border: 'rgba(207,59,46,0.16)',
        }
      : tone === 'warn'
      ? {
          background: 'linear-gradient(160deg, rgba(255,255,255,0.24) 0%, rgba(255,239,223,0.96) 100%)',
          accent: 'var(--warn)',
          label: 'text-warn',
          border: 'rgba(191,118,32,0.16)',
        }
      : tone === 'neutral'
      ? {
          background: 'linear-gradient(160deg, rgba(255,255,255,0.22) 0%, rgba(229,237,243,0.96) 100%)',
          accent: 'var(--ink-muted)',
          label: 'text-ink-muted',
          border: 'var(--border)',
        }
      : {
          background: `linear-gradient(160deg, rgba(255,255,255,0.24) 0%, ${sec.plate} 100%)`,
          accent: sec.color,
          label: '',
          border: sec.edge,
        };

  return (
    <section
      className={cx(
        'native-screen-enter overflow-hidden rounded-[28px] border px-4 py-4 shadow-card backdrop-blur-xl',
        className,
      )}
      style={{ background: styles.background, borderColor: styles.border }}
    >
      <div className="app-chip inline-flex items-center gap-2 rounded-full border border-border px-2.5 py-1 text-caption font-semibold uppercase tracking-[0.12em] ring-1 ring-white/30">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ background: styles.accent }}
          aria-hidden
        />
        <span className={styles.label}>{eyebrow}</span>
      </div>
      <div className="mt-3.5 text-[clamp(1.3rem,5.2vw,1.78rem)] font-semibold tracking-[-0.04em] leading-snug text-ink">
        {title}
      </div>
      {description && (
        <div className="mt-1.5 text-small leading-6 text-ink-muted">{description}</div>
      )}
      {meta && (
        <div className="app-shell-surface-soft mt-4 rounded-[18px] border border-border px-3 py-3 text-small text-ink-muted">
          {meta}
        </div>
      )}
      {actions && <div className="mt-4 flex flex-wrap items-center gap-2">{actions}</div>}
    </section>
  );
}

export function BatchTicket({
  to,
  section = 'batches',
  week,
  title,
  state,
  detail,
  note,
  footer,
}: {
  to: string;
  section?: SectionKey;
  week: string;
  title: string;
  state: string;
  detail?: ReactNode;
  note?: ReactNode;
  footer?: ReactNode;
}) {
  const sec = SECTIONS[section];

  return (
    <Link
      to={to}
      className="group native-screen-enter app-shell-surface block overflow-hidden rounded-[26px] border border-border p-4 shadow-card backdrop-blur-xl transition-all duration-150 hover:-translate-y-px"
      style={{ borderColor: sec.edge }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="app-chip inline-flex items-center rounded-full border border-border px-2.5 py-1 text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">
              {week}
            </span>
            <StatePill state={state} />
          </div>
          <div className="mt-3 text-h2 font-semibold tracking-[-0.02em] text-ink">{title}</div>
          {detail && <div className="mt-1 text-small text-ink-muted">{detail}</div>}
          {note && <div className="mt-3 text-small leading-6 text-ink-muted">{note}</div>}
        </div>
        <span
          className="app-chip mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border text-ink-muted transition-colors group-hover:text-ink"
          style={{ boxShadow: `0 12px 24px -20px ${sec.color}` }}
          aria-hidden
        >
          <ArrowRight className="h-4 w-4" strokeWidth={1.9} />
        </span>
      </div>
      {footer && <div className="mt-4 border-t border-border pt-3 text-caption text-ink-muted">{footer}</div>}
    </Link>
  );
}

export interface StickySummaryMetric {
  readonly label: string;
  readonly value: ReactNode;
  readonly tone?: 'default' | 'pos' | 'neg' | 'warn';
}

export function StickySummaryBar({
  section,
  title,
  subtitle,
  metrics,
  actions,
  className,
  stackedBelowTabs = false,
}: {
  section: SectionKey;
  title: ReactNode;
  subtitle?: ReactNode;
  metrics: readonly StickySummaryMetric[];
  actions?: ReactNode;
  className?: string;
  stackedBelowTabs?: boolean;
}) {
  const sec = SECTIONS[section];

  return (
    <div
      className={cx('sticky z-10 md:static', className)}
      style={{
        top: stackedBelowTabs
          ? 'calc(var(--mobile-topbar-height, 56px) + env(safe-area-inset-top) + var(--mobile-tabs-height, 56px) + 0.75rem)'
          : 'calc(var(--mobile-topbar-height, 56px) + env(safe-area-inset-top) + 0.5rem)',
      }}
    >
      <div
        className="overflow-hidden rounded-[28px] border border-border shadow-card backdrop-blur-2xl"
        style={{
          background: `linear-gradient(180deg, rgba(255,255,255,0.18) 0%, ${sec.dock} 100%)`,
          borderColor: sec.edge,
        }}
      >
        <div className="px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="app-chip inline-flex items-center gap-2 rounded-full border border-border px-2.5 py-1 text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: sec.color }}
                  aria-hidden
                />
                Summary
              </div>
              <div className="mt-3 text-h2 font-semibold tracking-[-0.03em] leading-tight text-ink">
                {title}
              </div>
              {subtitle && <div className="mt-1 text-caption text-ink-muted">{subtitle}</div>}
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5 min-[400px]:grid-cols-3">
            {metrics.map((metric) => (
              <div key={metric.label} className="app-shell-surface-soft min-w-0 rounded-[18px] border border-border px-3 py-3">
                <div className="truncate text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">{metric.label}</div>
                <div
                  className={cx(
                    'mt-1.5 min-w-0 truncate text-[clamp(0.92rem,4.1vw,1.32rem)] font-semibold tabular-nums tracking-[-0.02em] leading-none',
                    metric.tone === 'neg'
                      ? 'text-neg'
                      : metric.tone === 'pos'
                      ? 'text-pos'
                      : metric.tone === 'warn'
                      ? 'text-warn'
                      : 'text-ink',
                  )}
                >
                  {metric.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TimelineRow({
  section,
  title,
  subtitle,
  date,
  amount,
  amountTone = 'default',
  balance,
  badges,
  action,
}: {
  section: SectionKey;
  title: ReactNode;
  subtitle?: ReactNode;
  date: ReactNode;
  amount?: ReactNode;
  amountTone?: 'default' | 'pos' | 'neg' | 'warn';
  balance?: ReactNode;
  badges?: ReactNode;
  action?: ReactNode;
}) {
  const sec = SECTIONS[section];

  return (
    <div className="app-shell-surface-soft overflow-hidden rounded-[20px] border border-border px-4 py-3.5 shadow-sm backdrop-blur">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-body font-semibold tracking-[-0.02em] text-ink">{title}</div>
            {badges}
          </div>
          <div className="mt-1 text-caption text-ink-muted">{date}</div>
          {subtitle && <div className="mt-1.5 text-small leading-6 text-ink-muted">{subtitle}</div>}
        </div>
        {(amount || action) && (
          <div className="shrink-0 text-right">
            {amount && (
              <div
                className={cx(
                  'font-mono text-body font-semibold tabular-nums',
                  amountTone === 'neg'
                    ? 'text-neg'
                    : amountTone === 'pos'
                    ? 'text-pos'
                    : amountTone === 'warn'
                    ? 'text-warn'
                    : 'text-ink',
                )}
              >
                {amount}
              </div>
            )}
            {action}
          </div>
        )}
      </div>
      {balance && (
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3 text-caption text-ink-muted">
          <span style={{ color: sec.color }}>Balance after</span>
          <span className="font-mono text-small font-semibold text-ink">{balance}</span>
        </div>
      )}
    </div>
  );
}
